import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GuesteriaPartnersTable } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersTable";
import { GuesteriaPartnersFiltersPopover } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersFiltersPopover";
import { GuesteriaServicesTable } from "@/components/pages/guesteria-partners/components/GuesteriaServicesTable";
import { GuesteriaServicesFiltersPopover } from "@/components/pages/guesteria-partners/components/GuesteriaServicesFiltersPopover";
import {
  DEFAULT_GUESTERIA_PARTNERS_FILTERS,
  filterGuesteriaPartnerRows,
  type GuesteriaPartnersFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFilters";
import {
  DEFAULT_GUESTERIA_SERVICES_FILTERS,
  toPartnerServicesFilters,
  type GuesteriaServicesFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaServicesFilters";
import {
  mapPartnerSettingsToRow,
  providerSlugFromSettings,
} from "@/components/pages/guesteria-partners/utils/mapPartnerSettingsToGuesteria";
import {
  useGetPartnerSettings,
  useUpdatePartnerSettings,
  useGetPartnerServices,
  useGetPartnerServicesFilterOptions,
} from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { usePartnerRegionFlag } from "@/components/pages/guesteria-partners/hooks/usePartnerRegionFlag";
import { useHotel } from "@/hooks/useHotel";
import { BillingPageState } from "@/components/pages/billing-settings/components/BillingPageState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const GuesteriaPartnersPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const { data: settings, isLoading, isError } = useGetPartnerSettings();
  const { data: regionFlagEmoji } = usePartnerRegionFlag(
    hotelId,
    Boolean(
      settings?.destinationName?.trim() && !settings?.destination?.flagEmoji,
    ),
  );
  const updatePartnerSettings = useUpdatePartnerSettings();

  // Providers tab
  const [filters, setFilters] = useState<GuesteriaPartnersFiltersState>(
    DEFAULT_GUESTERIA_PARTNERS_FILTERS,
  );

  // Services tab
  const [servicesFilters, setServicesFilters] =
    useState<GuesteriaServicesFiltersState>(DEFAULT_GUESTERIA_SERVICES_FILTERS);
  const partnerServicesFilters = useMemo(
    () => toPartnerServicesFilters(servicesFilters),
    [servicesFilters],
  );
  const { data: servicesData, isLoading: servicesLoading } =
    useGetPartnerServices(partnerServicesFilters);
  const { data: filterOptions } = useGetPartnerServicesFilterOptions();

  const filteredPartners = useMemo(() => {
    if (!settings) {
      return [];
    }
    const rows = [mapPartnerSettingsToRow(settings, t, regionFlagEmoji)];
    return filterGuesteriaPartnerRows(rows, filters);
  }, [settings, filters, t, regionFlagEmoji]);

  const handlePartnerEnabledChange = useCallback(
    (id: string, enabled: boolean) => {
      if (!settings) {
        return;
      }
      if (providerSlugFromSettings(String(settings.provider)) !== id) {
        return;
      }
      updatePartnerSettings.mutate({ enabled });
    },
    [settings, updatePartnerSettings],
  );

  if (!hotelId) {
    return <BillingPageState text={t("partners.hotelNotSelected")} />;
  }

  if (isLoading) {
    return <BillingPageState text={t("partners.loading")} />;
  }

  if (isError) {
    return <BillingPageState text={t("partners.loadingError")} tone="error" />;
  }

  return (
    <div className="flex w-full flex-col gap-6 p-6">
      <Tabs defaultValue="providers" className="w-full">
        <TabsList className="grid w-full max-w-xs grid-cols-2">
          <TabsTrigger value="providers">
            {t("partners.tabs.providers")}
          </TabsTrigger>
          <TabsTrigger value="services">
            {t("partners.tabs.services")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="mt-6 space-y-6">
          <GuesteriaPartnersFiltersPopover
            applied={filters}
            onApply={setFilters}
          />
          <GuesteriaPartnersTable
            partners={filteredPartners}
            onPartnerEnabledChange={handlePartnerEnabledChange}
          />
        </TabsContent>

        <TabsContent value="services" className="mt-6 space-y-6">
          {filterOptions ? (
            <GuesteriaServicesFiltersPopover
              applied={servicesFilters}
              onApply={setServicesFilters}
              categories={filterOptions.categories}
              priceRange={filterOptions.priceRange}
            />
          ) : null}
          <GuesteriaServicesTable
            services={servicesData?.data ?? []}
            isLoading={servicesLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
